import { UNIT_SCALE, type KasaAccount, type KasaUnit } from '@omnia/shared';
import { LOCALE } from '../../lib/i18n.ts';

const integer = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/**
 * Plain tr-TR text of a minor-unit amount ("1.234,56"), built from integers so nothing rounds.
 * Fiat keeps two decimals; gold and Bitcoin show only the decimals they have, at least two.
 */
export function formatMinor(minor: number, scale: number, minFraction = 2): string {
  const sign = minor < 0 ? '-' : '';
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / 10 ** scale);
  let fraction = String(abs % 10 ** scale).padStart(scale, '0');
  const keep = Math.min(scale, minFraction);
  fraction = fraction.replace(/0+$/, '').padEnd(keep, '0');
  return `${sign}${integer.format(whole)}${fraction ? `,${fraction}` : ''}`;
}

type UnitLabel = Pick<KasaAccount, 'unit' | 'customName'>;

const SYMBOL: Record<Exclude<KasaUnit, 'CUSTOM' | 'XAU'>, string> = { TRY: '₺', USD: '$', EUR: '€', BTC: '₿' };

export const unitName = (account: UnitLabel) => (account.unit === 'CUSTOM' ? (account.customName ?? '?') : account.unit);

/** `₺1.234,56`, `$10,00`, `12,5 gr`, `0,015 ₿`, `40,00 PUAN`. */
export function formatAmount(minor: number, account: UnitLabel): string {
  const scale = UNIT_SCALE[account.unit];
  if (account.unit === 'TRY' || account.unit === 'USD' || account.unit === 'EUR') {
    const text = formatMinor(Math.abs(minor), scale);
    return `${minor < 0 ? '-' : ''}${SYMBOL[account.unit]}${text}`;
  }
  const text = formatMinor(minor, scale, account.unit === 'XAU' ? 0 : 2);
  return account.unit === 'XAU' ? `${text} gr` : account.unit === 'BTC' ? `${text} ₿` : `${text} ${unitName(account)}`;
}

/** Signed lira amount from kuruş, `₺1.234,56` or `-₺50,00`. */
export const formatTry = (kurus: number) => formatAmount(kurus, { unit: 'TRY', customName: null });

/** Whole lira for tight spaces (`₺1.234`), rounded. */
export const formatTryShort = (kurus: number) => `${kurus < 0 ? '-' : ''}₺${integer.format(Math.abs(Math.round(kurus / 100)))}`;

const percent = new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'exceptZero' });
export const formatPercent = (value: number) => `${percent.format(value)}%`;

/** Percent change of net worth against the previous snapshot; null when there is nothing to compare or divide by. */
export function changePercent(now: number, previous: number | null): number | null {
  if (previous === null || previous === 0) return null;
  return ((now - previous) / Math.abs(previous)) * 100;
}
